"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Farm = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const web3_js_1 = require("@solana/web3.js");
const common_1 = require("../common");
const entity_1 = require("../entity");
const marshmallow_1 = require("../marshmallow");
const spl_1 = require("../spl");
const id_1 = require("./id");
const layout_1 = require("./layout");
const logger = new common_1.Logger("Farm");
class Farm {
    /* ================= static functions ================= */
    static getProgramId(version) {
        const programId = id_1.FARM_VERSION_TO_PROGRAMID[version];
        if (!programId) {
            return logger.throwArgumentError("invalid version", "version", version);
        }
        return programId;
    }
    static getVersion(programId) {
        const programIdPubKey = (0, common_1.validateAndParsePublicKey)(programId);
        const programIdString = programIdPubKey.toBase58();
        const version = id_1.FARM_PROGRAMID_TO_VERSION[programIdString];
        if (!version) {
            return logger.throwArgumentError("invalid program id", "programId", programIdString);
        }
        return version;
    }
    static getStateLayout(version) {
        const STATE_LAYOUT = layout_1.FARM_VERSION_TO_STATE_LAYOUT[version];
        if (!STATE_LAYOUT) {
            return logger.throwArgumentError("invalid version", "version", version);
        }
        return STATE_LAYOUT;
    }
    static getLedgerLayout(version) {
        const LEDGER_LAYOUT = layout_1.FARM_VERSION_TO_LEDGER_LAYOUT[version];
        if (!LEDGER_LAYOUT) {
            return logger.throwArgumentError("invalid version", "version", version);
        }
        return LEDGER_LAYOUT;
    }
    static getLayouts(version) {
        return { state: this.getStateLayout(version), ledger: this.getLedgerLayout(version) };
    }
    static getAssociatedAuthority({ programId, poolId }) {
        return (0, common_1.findProgramAddress)([poolId.toBuffer()], programId);
    }
    static getAssociatedLedger({ programId, poolId, owner, }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { publicKey } = yield (0, common_1.findProgramAddress)([poolId.toBuffer(), owner.toBuffer(), Buffer.from("staker_info_v2_associated_seed", "utf-8")], programId);
            return publicKey;
        });
    }
    /* ================= instructions ================= */
    /* ================= deposit ================= */
    static makeDepositInstruction(params) {
        const { poolKeys } = params;
        const { version } = poolKeys;
        if (version === 3) {
            return this.makeDepositInstructionV3(params);
        }
        else if (version === 5) {
            return this.makeDepositInstructionV5(params);
        }
        return logger.throwArgumentError("invalid version", "poolKeys.version", version);
    }
    static makeDepositInstructionV3({ poolKeys, userKeys, amount }) {
        if (userKeys.rewardTokenAccounts.length !== 1) {
            return logger.throwArgumentError("lengths not equal 1", "userKeys.rewardTokenAccounts", userKeys.rewardTokenAccounts);
        }
        if (poolKeys.rewardVaults.length !== 1) {
            return logger.throwArgumentError("lengths not equal 1", "poolKeys.rewardVaults", poolKeys.rewardVaults);
        }
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction"), (0, marshmallow_1.u64)("amount")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 10,
            amount: (0, entity_1.parseBigNumberish)(amount),
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(poolKeys.authority, false),
            (0, common_1.AccountMeta)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMeta)(userKeys.lpTokenAccount, false),
            (0, common_1.AccountMeta)(poolKeys.lpVault, false),
            (0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[0], false),
            (0, common_1.AccountMeta)(poolKeys.rewardVaults[0], false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_CLOCK_PUBKEY, false),
            (0, common_1.AccountMetaReadonly)(common_1.TOKEN_PROGRAM_ID, false),
        ];
        if (userKeys.auxiliaryLedgers) {
            for (const auxiliaryLedger of userKeys.auxiliaryLedgers) {
                keys.push((0, common_1.AccountMeta)(auxiliaryLedger, false));
            }
        }
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    static makeDepositInstructionV5({ poolKeys, userKeys, amount }) {
        if (userKeys.rewardTokenAccounts.length !== poolKeys.rewardVaults.length) {
            return logger.throwArgumentError("lengths not equal with poolKeys.rewardVaults", "userKeys.rewardTokenAccounts", userKeys.rewardTokenAccounts);
        }
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction"), (0, marshmallow_1.u64)("amount")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 11,
            amount: (0, entity_1.parseBigNumberish)(amount),
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(poolKeys.authority, false),
            (0, common_1.AccountMeta)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMeta)(userKeys.lpTokenAccount, false),
            (0, common_1.AccountMeta)(poolKeys.lpVault, false),
            (0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[0], false),
            (0, common_1.AccountMeta)(poolKeys.rewardVaults[0], false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_CLOCK_PUBKEY, false),
            (0, common_1.AccountMetaReadonly)(common_1.TOKEN_PROGRAM_ID, false),
        ];
        for (let index = 1; index < poolKeys.rewardVaults.length; index++) {
            keys.push((0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[index], false));
            keys.push((0, common_1.AccountMeta)(poolKeys.rewardVaults[index], false));
        }
        if (userKeys.auxiliaryLedgers) {
            for (const auxiliaryLedger of userKeys.auxiliaryLedgers) {
                keys.push((0, common_1.AccountMeta)(auxiliaryLedger, false));
            }
        }
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    /* ================= withdraw ================= */
    static makeWithdrawInstruction(params) {
        const { poolKeys } = params;
        const { version } = poolKeys;
        if (version === 3) {
            return this.makeWithdrawInstructionV3(params);
        }
        else if (version === 5) {
            return this.makeWithdrawInstructionV5(params);
        }
        return logger.throwArgumentError("invalid version", "poolKeys.version", version);
    }
    static makeWithdrawInstructionV3({ poolKeys, userKeys, amount }) {
        if (userKeys.rewardTokenAccounts.length !== 1) {
            return logger.throwArgumentError("lengths not equal 1", "userKeys.rewardTokenAccounts", userKeys.rewardTokenAccounts);
        }
        if (poolKeys.rewardVaults.length !== 1) {
            return logger.throwArgumentError("lengths not equal 1", "poolKeys.rewardVaults", poolKeys.rewardVaults);
        }
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction"), (0, marshmallow_1.u64)("amount")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 11,
            amount: (0, entity_1.parseBigNumberish)(amount),
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(poolKeys.authority, false),
            (0, common_1.AccountMeta)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMeta)(userKeys.lpTokenAccount, false),
            (0, common_1.AccountMeta)(poolKeys.lpVault, false),
            (0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[0], false),
            (0, common_1.AccountMeta)(poolKeys.rewardVaults[0], false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_CLOCK_PUBKEY, false),
            (0, common_1.AccountMetaReadonly)(common_1.TOKEN_PROGRAM_ID, false),
        ];
        if (userKeys.auxiliaryLedgers) {
            for (const auxiliaryLedger of userKeys.auxiliaryLedgers) {
                keys.push((0, common_1.AccountMeta)(auxiliaryLedger, false));
            }
        }
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    static makeWithdrawInstructionV5({ poolKeys, userKeys, amount }) {
        if (userKeys.rewardTokenAccounts.length !== poolKeys.rewardVaults.length) {
            return logger.throwArgumentError("lengths not equal with params.poolKeys.rewardVaults", "userKeys.rewardTokenAccounts", userKeys.rewardTokenAccounts);
        }
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction"), (0, marshmallow_1.u64)("amount")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 12,
            amount: (0, entity_1.parseBigNumberish)(amount),
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(poolKeys.authority, false),
            (0, common_1.AccountMeta)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMeta)(userKeys.lpTokenAccount, false),
            (0, common_1.AccountMeta)(poolKeys.lpVault, false),
            (0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[0], false),
            (0, common_1.AccountMeta)(poolKeys.rewardVaults[0], false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_CLOCK_PUBKEY, false),
            (0, common_1.AccountMetaReadonly)(common_1.TOKEN_PROGRAM_ID, false),
        ];
        for (let index = 1; index < poolKeys.rewardVaults.length; index++) {
            keys.push((0, common_1.AccountMeta)(userKeys.rewardTokenAccounts[index], false));
            keys.push((0, common_1.AccountMeta)(poolKeys.rewardVaults[index], false));
        }
        if (userKeys.auxiliaryLedgers) {
            for (const auxiliaryLedger of userKeys.auxiliaryLedgers) {
                keys.push((0, common_1.AccountMeta)(auxiliaryLedger, false));
            }
        }
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    /* ================= create associated ledger account ================= */
    static makeCreateAssociatedLedgerAccountInstruction(params) {
        const { poolKeys } = params;
        const { version } = poolKeys;
        if (version === 3) {
            return this.makeCreateAssociatedLedgerAccountInstructionV3(params);
        }
        else if (version === 5) {
            return this.makeCreateAssociatedLedgerAccountInstructionV5(params);
        }
        return logger.throwArgumentError("invalid version", "poolKeys.version", version);
    }
    static makeCreateAssociatedLedgerAccountInstructionV3({ poolKeys, userKeys, }) {
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 9,
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMetaReadonly)(common_1.SYSTEM_PROGRAM_ID, false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_RENT_PUBKEY, false),
        ];
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    static makeCreateAssociatedLedgerAccountInstructionV5({ poolKeys, userKeys, }) {
        const LAYOUT = (0, marshmallow_1.struct)([(0, marshmallow_1.u8)("instruction")]);
        const data = Buffer.alloc(LAYOUT.span);
        LAYOUT.encode({
            instruction: 10,
        }, data);
        const keys = [
            (0, common_1.AccountMeta)(poolKeys.id, false),
            (0, common_1.AccountMetaReadonly)(userKeys.ledger, false),
            (0, common_1.AccountMetaReadonly)(userKeys.owner, true),
            (0, common_1.AccountMetaReadonly)(common_1.SYSTEM_PROGRAM_ID, false),
            (0, common_1.AccountMetaReadonly)(common_1.SYSVAR_RENT_PUBKEY, false),
        ];
        return new web3_js_1.TransactionInstruction({
            programId: poolKeys.programId,
            keys,
            data,
        });
    }
    static getMultipleInfo({ connection, pools, owner, config }) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKeys = [];
            for (const pool of pools) {
                publicKeys.push({
                    pubkey: pool.id,
                    version: pool.version,
                    key: "state",
                    poolId: pool.id,
                });
                publicKeys.push({
                    pubkey: pool.lpVault,
                    version: pool.version,
                    key: "lpVault",
                    poolId: pool.id,
                });
                if (owner) {
                    publicKeys.push({
                        pubkey: yield this.getAssociatedLedger({ programId: pool.programId, poolId: pool.id, owner }),
                        version: pool.version,
                        key: "ledger",
                        poolId: pool.id,
                    });
                }
            }
            const poolsInfo = {};
            const accountsInfo = yield (0, common_1.getMultipleAccountsInfoWithCustomFlag)(connection, publicKeys, config);
            for (const { pubkey, version, key, poolId, accountInfo } of accountsInfo) {
                if (key === "state") {
                    const STATE_LAYOUT = this.getStateLayout(version);
                    if (!accountInfo || !accountInfo.data || accountInfo.data.length !== STATE_LAYOUT.span) {
                        return logger.throwArgumentError("invalid farm state account info", "pools.id", pubkey.toBase58());
                    }
                    poolsInfo[poolId.toBase58()] = Object.assign(Object.assign({}, poolsInfo[poolId.toBase58()]), { state: STATE_LAYOUT.decode(accountInfo.data) });
                }
                else if (key === "lpVault") {
                    if (!accountInfo || !accountInfo.data || accountInfo.data.length !== spl_1.SPL_ACCOUNT_LAYOUT.span) {
                        return logger.throwArgumentError("invalid farm lp vault account info", "pools.lpVault", pubkey.toBase58());
                    }
                    poolsInfo[poolId.toBase58()] = Object.assign(Object.assign({}, poolsInfo[poolId.toBase58()]), { lpVault: spl_1.SPL_ACCOUNT_LAYOUT.decode(accountInfo.data) });
                }
                else if (key === "ledger") {
                    const LEDGER_LAYOUT = this.getLedgerLayout(version);
                    if (accountInfo && accountInfo.data) {
                        if (accountInfo.data.length !== LEDGER_LAYOUT.span) {
                            return logger.throwArgumentError("invalid farm ledger account info", "ledger", pubkey.toBase58());
                        }
                        poolsInfo[poolId.toBase58()] = Object.assign(Object.assign({}, poolsInfo[poolId.toBase58()]), { ledger: LEDGER_LAYOUT.decode(accountInfo.data) });
                    }
                }
            }
            // wrapped data
            for (const [poolId, { state, ledger }] of Object.entries(poolsInfo)) {
                if (ledger) {
                    const multiplier = entity_1.TEN.pow(new bn_js_1.default(15));
                    const pendingRewards = state.perShareRewards.map((perShareReward, index) => {
                        const rewardDebt = ledger.rewardDebts[index];
                        const pendingReward = ledger.deposited.mul(perShareReward).div(multiplier).sub(rewardDebt);
                        return pendingReward;
                    });
                    poolsInfo[poolId].wrapped = Object.assign(Object.assign({}, poolsInfo[poolId].wrapped), { pendingRewards });
                }
            }
            return poolsInfo;
        });
    }
}
exports.Farm = Farm;
//# sourceMappingURL=farm.js.map